import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Alert,
  IconButton,
  Chip,
  useTheme,
  InputAdornment,
  Tooltip,
  Button,
  Link as MuiLink,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Search,
  Edit,
  Delete,
  Add,
  Folder as FolderIcon,
  Description,
  OpenInNew,
  ViewList,
  AccountTree,
  FolderOpen,
  PictureAsPdf,
  InsertDriveFile,
  ExpandMore,
  ChevronRight,
} from '@mui/icons-material';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_PaginationState,
} from 'material-react-table';
import { ConfirmDialog } from '@/components/common';
import { KnowledgeBaseDialog } from '@/components/knowledgeBase/KnowledgeBaseDialog';
import { useAppDispatch, useAppSelector } from '@/store/types';
import {
  fetchKnowledgeBaseLinks,
  createKnowledgeBaseLink,
  updateKnowledgeBaseLink,
  deleteKnowledgeBaseLink,
  setCurrentLink,
  clearKnowledgeBaseError,
  setUploading,
} from '@/store';
import {
  KnowledgeBaseLink,
  KnowledgeBaseLinkFormData,
  FileReference,
  UploadedFile,
} from '@/constants/types/knowledgeBase.types';
import { folderService } from '@/services/admin/folderService';
import { uploadService } from '@/services/upload/uploadService';
import { Folder } from '@/constants/types/folder.types';
import toast from 'react-hot-toast';

interface KnowledgeBaseTreeViewProps {
  links: KnowledgeBaseLink[];
  folders: Folder[];
  onEdit: (link: KnowledgeBaseLink) => void;
  onDelete: (link: KnowledgeBaseLink) => void;
  isLoading: boolean;
}

const KnowledgeBaseTreeView: React.FC<KnowledgeBaseTreeViewProps> = ({
  links,
  folders,
  onEdit,
  onDelete,
  isLoading,
}) => {
  const theme = useTheme();
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());

  const toggleFolder = (folderId: string) => {
    setExpandedFolders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  };

  // Build folder hierarchy
  const buildFolderTree = (folders: Folder[]): Folder[] => {
    const folderMap = new Map<string, Folder & { children: Folder[] }>();
    const rootFolders: (Folder & { children: Folder[] })[] = [];

    // Initialize all folders with children array
    folders.forEach((folder) => {
      folderMap.set(folder._id, { ...folder, children: [] });
    });

    // Build tree structure
    folders.forEach((folder) => {
      const folderWithChildren = folderMap.get(folder._id)!;
      if (folder.parentId && folderMap.has(folder.parentId)) {
        folderMap.get(folder.parentId)!.children.push(folderWithChildren);
      } else {
        rootFolders.push(folderWithChildren);
      }
    });

    return rootFolders;
  };

  // Group links by folder
  const linksByFolder = useMemo(() => {
    const map = new Map<string, KnowledgeBaseLink[]>();
    links.forEach((link) => {
      const folderId = typeof link.folderId === 'string' ? link.folderId : link.folderId._id;
      if (!map.has(folderId)) {
        map.set(folderId, []);
      }
      map.get(folderId)!.push(link);
    });
    return map;
  }, [links]);

  // Get file icon based on mime type
  const getFileIcon = (mimeType: string) => {
    if (mimeType === 'application/pdf') {
      return <PictureAsPdf sx={{ fontSize: 20, color: '#d32f2f' }} />;
    }
    return <InsertDriveFile sx={{ fontSize: 20, color: '#1976d2' }} />;
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Format date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const renderFolder = (folder: Folder & { children?: Folder[] }, level: number = 0) => {
    const isExpanded = expandedFolders.has(folder._id);
    const folderLinks = linksByFolder.get(folder._id) || [];
    const hasChildren = (folder.children && folder.children.length > 0) || folderLinks.length > 0;

    return (
      <Box key={folder._id}>
        {/* Folder Row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            py: 0.75,
            px: 1.5,
            pl: 1.5 + level * 2.5,
            cursor: hasChildren ? 'pointer' : 'default',
            userSelect: 'none',
            transition: 'all 0.15s ease-in-out',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(0, 0, 0, 0.04)',
            },
          }}
          onClick={() => hasChildren && toggleFolder(folder._id)}
        >
          {/* Expand/Collapse Icon */}
          <Box
            sx={{
              width: 24,
              height: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 0.5,
              visibility: hasChildren ? 'visible' : 'hidden',
            }}
          >
            {isExpanded ? (
              <ExpandMore sx={{ fontSize: 18, color: 'text.secondary' }} />
            ) : (
              <ChevronRight sx={{ fontSize: 18, color: 'text.secondary' }} />
            )}
          </Box>

          {/* Folder Icon */}
          {isExpanded ? (
            <FolderOpen sx={{ fontSize: 22, color: '#FFA726', mr: 1 }} />
          ) : (
            <FolderIcon sx={{ fontSize: 22, color: '#FFA726', mr: 1 }} />
          )}

          {/* Folder Name */}
          <Typography
            variant="body2"
            fontWeight={500}
            sx={{
              flex: 1,
              color: 'text.primary',
            }}
          >
            {folder.name}
          </Typography>

          {/* Document Count Badge */}
          {folderLinks.length > 0 && (
            <Chip
              label={folderLinks.length}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.7rem',
                fontWeight: 600,
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(144, 202, 249, 0.16)'
                  : 'rgba(25, 118, 210, 0.12)',
                color: 'primary.main',
              }}
            />
          )}
        </Box>

        {/* Expanded Content */}
        {isExpanded && (
          <Box>
            {/* Document Items */}
            {folderLinks.map((link) => (
              <Box
                key={link._id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1,
                  px: 1.5,
                  pl: 1.5 + (level + 1) * 2.5 + 2.5,
                  transition: 'all 0.15s ease-in-out',
                  borderLeft: `2px solid transparent`,
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.08)'
                      : 'rgba(0, 0, 0, 0.03)',
                    borderLeftColor: 'primary.main',
                    '& .action-buttons': {
                      opacity: 1,
                    },
                  },
                }}
              >
                {/* Left Section: File Info */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                  {/* File Type Icon */}
                  {getFileIcon(link.file.mimeType)}

                  {/* File Details */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        sx={{
                          color: 'text.primary',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {link.title}
                      </Typography>
                    </Box>

                    {/* File Metadata */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.25 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.7rem',
                        }}
                      >
                        {link.file.originalName}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.7rem',
                        }}
                      >
                        •
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.7rem',
                        }}
                      >
                        {formatFileSize(link.file.size)}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.7rem',
                        }}
                      >
                        •
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.7rem',
                        }}
                      >
                        {formatDate(link.createdAt)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Right Section: Actions */}
                <Box
                  className="action-buttons"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    ml: 2,
                    opacity: 0,
                    transition: 'opacity 0.2s',
                  }}
                >
                  <Tooltip title="Download / View">
                    <IconButton
                      size="small"
                      component="a"
                      href={link.file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        color: 'text.secondary',
                        '&:hover': {
                          color: 'primary.main',
                          backgroundColor: theme.palette.mode === 'dark'
                            ? 'rgba(144, 202, 249, 0.16)'
                            : 'rgba(25, 118, 210, 0.08)',
                        },
                      }}
                    >
                      <OpenInNew sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(link);
                      }}
                      sx={{
                        color: 'text.secondary',
                        '&:hover': {
                          color: 'primary.main',
                          backgroundColor: theme.palette.mode === 'dark'
                            ? 'rgba(144, 202, 249, 0.16)'
                            : 'rgba(25, 118, 210, 0.08)',
                        },
                      }}
                    >
                      <Edit sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(link);
                      }}
                      sx={{
                        color: 'text.secondary',
                        '&:hover': {
                          color: 'error.main',
                          backgroundColor: theme.palette.mode === 'dark'
                            ? 'rgba(244, 67, 54, 0.16)'
                            : 'rgba(211, 47, 47, 0.08)',
                        },
                      }}
                    >
                      <Delete sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            ))}

            {/* Child Folders */}
            {folder.children && folder.children.map((child) => renderFolder(child, level + 1))}
          </Box>
        )}
      </Box>
    );
  };

  const folderTree = useMemo(() => buildFolderTree(folders), [folders]);

  if (isLoading) {
    return (
      <Box
        sx={{
          p: 4,
          textAlign: 'center',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  if (folderTree.length === 0) {
    return (
      <Box
        sx={{
          p: 4,
          textAlign: 'center',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <FolderIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          No folders found. Create folders first to organize your knowledge base.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        overflow: 'hidden',
        backgroundColor: theme.palette.background.paper,
      }}
    >
      {folderTree.map((folder) => renderFolder(folder))}
    </Box>
  );
};

export const KnowledgeBasePage: React.FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const { links, pagination, isLoading, isActionLoading, isUploading, error } =
    useAppSelector((state) => state.knowledgeBase);

  const [searchFilter, setSearchFilter] = useState('');
  const [folderFilter, setFolderFilter] = useState<string>('');
  const [paginationState, setPaginationState] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [viewMode, setViewMode] = useState<'table' | 'tree'>('table');

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedLink, setSelectedLink] = useState<KnowledgeBaseLink | null>(null);
  const [allFolders, setAllFolders] = useState<Folder[]>([]);

  useEffect(() => {
    loadLinks();
  }, [searchFilter, folderFilter, paginationState]);

  useEffect(() => {
    loadAllFolders();
  }, []);

  const loadLinks = () => {
    dispatch(
      fetchKnowledgeBaseLinks({
        page: paginationState.pageIndex + 1,
        limit: paginationState.pageSize,
        sort: '-createdAt',
        ...(searchFilter && { search: searchFilter }),
        ...(folderFilter && { folderId: folderFilter }),
      })
    );
  };

  const loadAllFolders = async () => {
    try {
      const response = await folderService.getFolders({
        limit: 1000,
        sort: 'level,name',
        groupByParent: false,
      });
      if (response.success) {
        setAllFolders(response.data.folders);
      }
    } catch (err) {
      console.error('Failed to load folders:', err);
    }
  };

  const handleCreateClick = () => {
    setCreateDialogOpen(true);
  };

  const handleCreateSubmit = async (data: KnowledgeBaseLinkFormData) => {
    try {
      if (!data.document) {
        toast.error('Document is required');
        return;
      }

      // Upload document
      dispatch(setUploading(true));
      const folder = 'feeds/knowledge';
      const uploadResponse = await uploadService.uploadSingle(data.document, {
        folder,
      });

      if (!uploadResponse.success || !uploadResponse.file) {
        dispatch(setUploading(false));
        toast.error('Failed to upload document');
        return;
      }

      dispatch(setUploading(false));

      const result = await dispatch(
        createKnowledgeBaseLink({
          folderId: data.folderId,
          title: data.title,
          description: data.description,
          file: uploadResponse.file,
        })
      ).unwrap();

      toast.success(result.message || 'Knowledge base link created successfully');
      setCreateDialogOpen(false);
      loadLinks();
    } catch (err: any) {
      dispatch(setUploading(false));
      toast.error(err.message || 'Failed to create knowledge base link');
    }
  };

  const handleEditClick = (link: KnowledgeBaseLink) => {
    dispatch(setCurrentLink(link));
    setSelectedLink(link);
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (data: KnowledgeBaseLinkFormData) => {
    if (!selectedLink) return;

    try {
      let fileObject: FileReference | UploadedFile = selectedLink.file;

      // Upload new document if provided
      if (data.document) {
        dispatch(setUploading(true));
        const folder = 'feeds/knowledge';
        const uploadResponse = await uploadService.uploadSingle(data.document, {
          folder,
        });

        if (!uploadResponse.success || !uploadResponse.file) {
          dispatch(setUploading(false));
          toast.error('Failed to upload document');
          return;
        }

        fileObject = uploadResponse.file;
        dispatch(setUploading(false));
      }

      const result = await dispatch(
        updateKnowledgeBaseLink({
          id: selectedLink._id,
          data: {
            folderId: data.folderId,
            title: data.title,
            description: data.description,
            file: fileObject,
          },
        })
      ).unwrap();

      toast.success(result.message || 'Knowledge base link updated successfully');
      setEditDialogOpen(false);
      setSelectedLink(null);
      dispatch(setCurrentLink(null));
      loadLinks();
    } catch (err: any) {
      dispatch(setUploading(false));
      toast.error(err.message || 'Failed to update knowledge base link');
    }
  };

  const handleDeleteClick = (link: KnowledgeBaseLink) => {
    setSelectedLink(link);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedLink) return;

    try {
      await dispatch(deleteKnowledgeBaseLink(selectedLink._id)).unwrap();

      // Delete the associated file from storage
      try {
        await uploadService.deleteSingleFile(selectedLink.file.id);
      } catch (fileErr) {
        console.error('Failed to delete file from storage:', fileErr);
        // Continue even if file deletion fails
      }

      toast.success('Knowledge base link deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedLink(null);
      loadLinks();
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete knowledge base link');
    }
  };

  const handleDialogClose = () => {
    setCreateDialogOpen(false);
    setEditDialogOpen(false);
    setDeleteDialogOpen(false);
    setSelectedLink(null);
    dispatch(setCurrentLink(null));
  };

  const handleErrorDismiss = () => {
    dispatch(clearKnowledgeBaseError());
  };

  const getFolderName = (folderId: string | { _id: string; name: string; description: string }): string => {
    // If folderId is already a populated object, return its name
    if (typeof folderId === 'object' && folderId !== null) {
      return folderId.name;
    }
    // Otherwise, look it up in allFolders
    const folder = allFolders.find((f) => f._id === folderId);
    return folder ? folder.name : 'Unknown';
  };

  const columns = useMemo<MRT_ColumnDef<KnowledgeBaseLink>[]>(
    () => [
      {
        accessorKey: 'title',
        header: 'Title',
        size: 250,
        Cell: ({ row }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Description sx={{ color: 'primary.main', fontSize: 24 }} />
            <Typography variant="body2" fontWeight="medium">
              {row.original.title}
            </Typography>
          </Box>
        ),
      },
      {
        accessorKey: 'folderId',
        header: 'Folder',
        size: 150,
        Cell: ({ row }) => (
          <Chip
            icon={<FolderIcon />}
            label={getFolderName(row.original.folderId)}
            size="small"
            variant="outlined"
          />
        ),
      },
      {
        accessorKey: 'file',
        header: 'Document',
        size: 250,
        Cell: ({ row }) => {
          const file = row.original.file;

          return (
            <Box>
              <MuiLink
                href={file.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                  {file.originalName}
                </Typography>
                <OpenInNew fontSize="small" />
              </MuiLink>
              <Typography variant="caption" color="text.secondary">
                {(file.size / 1024).toFixed(2)} KB
              </Typography>
            </Box>
          );
        },
      },
      {
        accessorKey: 'description',
        header: 'Description',
        size: 300,
        Cell: ({ cell }) => (
          <Typography variant="body2" color="text.secondary" noWrap>
            {cell.getValue<string>() || '-'}
          </Typography>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Created At',
        size: 150,
        Cell: ({ cell }) => (
          <Typography variant="body2" color="text.secondary">
            {new Date(cell.getValue<string>()).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })}
          </Typography>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        size: 100,
        enableSorting: false,
        Cell: ({ row }) => (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Edit Link">
              <IconButton
                size="small"
                onClick={() => handleEditClick(row.original)}
                sx={{ color: 'primary.main' }}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Link">
              <IconButton
                size="small"
                onClick={() => handleDeleteClick(row.original)}
                sx={{ color: 'error.main' }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [allFolders]
  );

  const table = useMaterialReactTable({
    columns,
    data: links,
    enableRowSelection: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableGlobalFilter: false,
    enablePagination: true,
    enableSorting: false,
    enableBottomToolbar: true,
    enableTopToolbar: false,
    manualPagination: true,
    rowCount: pagination?.total || 0,
    muiTableBodyRowProps: {
      sx: {
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
      },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        border: `1px solid ${theme.palette.divider}`,
      },
    },
    muiTableProps: {
      sx: {
        tableLayout: 'fixed',
      },
    },
    state: {
      isLoading,
      pagination: paginationState,
    },
    onPaginationChange: setPaginationState,
  });

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4" fontWeight="bold">
            Knowledge Base
          </Typography>
          <Chip
            label={`${pagination?.total || 0} Resources`}
            color="primary"
            size="small"
          />
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreateClick}
          sx={{ textTransform: 'none' }}
        >
          Add Resource
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={handleErrorDismiss}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              fullWidth
              placeholder="Search resources..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                },
              }}
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Filter by Folder"
              value={folderFilter}
              onChange={(e) => setFolderFilter(e.target.value)}
            >
              <MenuItem value="">
                <em>All Folders</em>
              </MenuItem>
              {allFolders.map((folder) => (
                <MenuItem key={folder._id} value={folder._id}>
                  {'  '.repeat(folder.level - 1)}
                  {folder.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 5 }} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, newMode) => newMode && setViewMode(newMode)}
              size="small"
              aria-label="view mode"
            >
              <ToggleButton value="table" aria-label="table view">
                <ViewList sx={{ mr: 1 }} />
                Table View
              </ToggleButton>
              <ToggleButton value="tree" aria-label="tree view">
                <AccountTree sx={{ mr: 1 }} />
                Tree View
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
        </Grid>
      </Box>

      {viewMode === 'table' ? (
        <MaterialReactTable table={table} />
      ) : (
        <KnowledgeBaseTreeView
          links={links}
          folders={allFolders}
          onEdit={handleEditClick}
          onDelete={handleDeleteClick}
          isLoading={isLoading}
        />
      )}

      <KnowledgeBaseDialog
        open={createDialogOpen || editDialogOpen}
        link={editDialogOpen ? selectedLink : null}
        folders={allFolders}
        onClose={handleDialogClose}
        onSubmit={editDialogOpen ? handleEditSubmit : handleCreateSubmit}
        isLoading={isActionLoading}
        isUploading={isUploading}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Knowledge Base Link"
        message={`Are you sure you want to delete "${selectedLink?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDialogClose}
        severity="error"
      />
    </Box>
  );
};

export default KnowledgeBasePage;
